import { Controller } from "@hotwired/stimulus"

// Internal Expenses tab: add/edit/delete entries with receipt uploads
// (presign → S3 PUT → attach on save), client-side search, and the office's
// verify toggle. All actions update the DOM in place — no page reloads.
export default class extends Controller {
  static targets = ["tbody", "row", "empty", "total", "countNote", "search", "filterBox",
                    "modal", "modalTitle", "vendor", "amount", "invoiceDate", "invoiceNumber",
                    "description", "dropzone", "fileInput", "chips", "saveBtn",
                    "deleteModal", "deleteNote", "moveModal", "moveNote",
                    "moveVendor", "moveMatch", "moveForceNewRow", "moveForceNew", "moveAmount", "moveNumber", "moveDate", "moveDesc",
                    "moveZone", "moveInput", "moveChips", "movePaid", "moveReview", "moveBtn", "moveSeq",
                    "pick", "pickAll", "bulkMoveBtn", "bulkMoveLabel", "bulkMoveModal",
                    "bulkMoveList", "bulkMovePaid", "bulkMoveGo",
                    "selectToggle", "bulkBar", "bulkCount",
                    "bulkDeleteBtn", "bulkDeleteModal", "bulkDeleteNote", "bulkDeleteGo",
                    "aiBox", "aiFileInput", "aiStatus",
                    "aiImportModal", "aiImportZone", "aiImportFileInput", "aiImportStatus",
                    "aiImportSkip", "aiImportBtn", "aiImportError"]
  static values = { matchUrl: String, createUrl: String, presignUrl: String, readUrl: String, aiImportUrl: String }

  connect() {
    this._files = []          // freshly uploaded, not yet attached: {cache_name, filename}
    this._editingId = null
    this._deleteId = null
    this._renumber()
  }

  // ── Search ──────────────────────────────────────────────────────────
  filter() {
    const q = this.searchTarget.value.trim().toLowerCase()
    let shown = 0
    this.rowTargets.forEach((tr) => {
      const hit = !q || (tr.dataset.search || "").includes(q)
      tr.classList.toggle("hidden", !hit)
      if (hit) shown += 1
    })
    this._renumber()
    if (this._selectMode) this._syncBulkBar()
  }

  // K3-106 (Larry 2026-10-30): while a search is on, the box next to the
  // running total shows the matching vendor, invoice count and their total.
  // One vendor across every match → its name; otherwise what was typed.
  // Runs from _renumber, so adds/edits/deletes keep it (and "x of y") current.
  _syncFilterBox() {
    if (!this.hasSearchTarget) return
    const q = this.searchTarget.value.trim()
    const all = this.rowTargets
    const shown = all.filter((tr) => !tr.classList.contains("hidden"))
    this.countNoteTarget.textContent = q ? `${shown.length} of ${all.length}` : all.length
    if (!this.hasFilterBoxTarget) return
    const box = this.filterBoxTarget
    box.classList.toggle("hidden", !q)
    box.classList.toggle("inline-flex", !!q)
    if (!q) return
    let total = 0
    const vendors = new Set()
    shown.forEach((tr) => {
      let d = {}
      try { d = JSON.parse(tr.dataset.expense || "{}") } catch (_) { /* keep 0 */ }
      total += Number(d.amount) || 0
      vendors.add((d.vendor || "").trim())
    })
    const one = vendors.size === 1 ? [...vendors][0] : ""
    box.querySelector("[data-filter-label]").textContent = one || `“${q}”`
    box.querySelector("[data-filter-count]").textContent =
      `${shown.length} ${shown.length === 1 ? box.dataset.one : box.dataset.many}`
    box.querySelector("[data-filter-total]").textContent =
      total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  // ── Add / edit modal ────────────────────────────────────────────────
  openNew() {
    this._editingId = null
    this.modalTitleTarget.textContent = "Add expense"
    this.vendorTarget.value = ""
    this.amountTarget.value = ""
    this._setDate("")
    this.invoiceNumberTarget.value = ""
    this.descriptionTarget.value = ""
    this._files = []
    this.chipsTarget.innerHTML = ""
    this._resetAiStatus()
    this.modalTarget.classList.remove("hidden")
  }

  openEdit(e) {
    const row = e.currentTarget.closest("tr")
    const data = JSON.parse(row.dataset.expense || "{}")
    this._editingId = data.id
    this.modalTitleTarget.textContent = "Edit expense"
    this.vendorTarget.value = data.vendor || ""
    this.amountTarget.value = data.amount != null ? data.amount.toFixed(2) : ""
    this._setDate(data.invoice_date || "")
    this.invoiceNumberTarget.value = data.invoice_number || ""
    this.descriptionTarget.value = data.description || ""
    this._files = []
    this.chipsTarget.innerHTML = ""
    ;(data.receipts || []).forEach((r) => this._addExistingChip(r))
    this._resetAiStatus()
    this.modalTarget.classList.remove("hidden")
  }

  // ── AI drop zone (Phelipe 2026-07-28): drop a receipt → presign → S3 PUT
  // → OCR-read → the fields fill themselves; the file stays attached as the
  // receipt. Same tech as the sub portal readers. ────────────────────────
  pickAiFile() { this.aiFileInputTarget.click() }

  aiDragOver(e) {
    e.preventDefault()
    this.aiBoxTarget.classList.add("border-purple-500", "bg-purple-100")
  }

  aiDragLeave() { this.aiBoxTarget.classList.remove("border-purple-500", "bg-purple-100") }

  aiDrop(e) {
    e.preventDefault()
    this.aiDragLeave()
    const file = e.dataTransfer?.files?.[0]
    if (file) this._runAiRead(file)
  }

  aiFileChosen(e) {
    const file = e.target.files[0]
    if (file) this._runAiRead(file)
    e.target.value = ""
  }

  _resetAiStatus() {
    if (!this.hasAiStatusTarget) return
    this.aiStatusTarget.textContent = ""
    this.aiStatusTarget.className = "hidden text-[13px] text-purple-700 mt-1.5"
  }

  _sayAi(text, cls = "text-purple-700") {
    if (!this.hasAiStatusTarget) return
    this.aiStatusTarget.className = `text-[13px] ${cls} mt-1.5`
    this.aiStatusTarget.textContent = text
  }

  async _runAiRead(file) {
    const chip = this._makeChip(file.name, true)
    this.chipsTarget.appendChild(chip)
    this._sayAi(`Uploading ${file.name}…`)
    try {
      const presign = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type, byte_size: file.size }] })
      })
      const pd = await presign.json()
      if (!presign.ok) throw new Error(pd.error || "Upload refused")
      const up = pd.uploads[0]
      const put = await fetch(up.upload_url, { method: "PUT", body: file })
      if (!put.ok) throw new Error(`Upload failed for ${file.name}`)
      // The file rides as a receipt chip exactly like a manual upload.
      this._files.push({ cache_name: up.cache_name, filename: up.filename })
      this._finishChip(chip, () => {
        this._files = this._files.filter((f) => f.cache_name !== up.cache_name)
        chip.remove()
      })

      this._sayAi("✨ Reading your file…")
      const resp = await fetch(this.readUrlValue, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify({ cache_name: up.cache_name, filename: up.filename })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't read the file")
      if (data.vendor) this.vendorTarget.value = data.vendor
      if (data.amount != null) this.amountTarget.value = Number(data.amount).toFixed(2)
      if (data.invoice_date) this._setDate(data.invoice_date)
      if (data.invoice_number) this.invoiceNumberTarget.value = data.invoice_number
      if (data.description) this.descriptionTarget.value = data.description
      this._sayAi(`✓ Filled from ${file.name} — review and save. File attached as the receipt.`, "text-green-600")
    } catch (err) {
      if (!this._files.some((f) => f.filename === file.name)) chip.remove()
      this._sayAi(err.message)
    }
  }

  closeModal() { this.modalTarget.classList.add("hidden") }
  stopPropagation(e) { e.stopPropagation() }

  _setDate(ymd) {
    const input = this.invoiceDateTarget
    if (input._flatpickr) input._flatpickr.setDate(ymd || null, false)
    else input.value = ymd
  }

  async save() {
    const btn = this.saveBtnTarget
    btn.disabled = true
    btn.textContent = "Saving..."
    const body = {
      vendor: this.vendorTarget.value,
      amount: this.amountTarget.value,
      invoice_date: this._dateValue(),
      invoice_number: this.invoiceNumberTarget.value,
      description: this.descriptionTarget.value,
      files: this._files
    }
    const url = this._editingId ? `${this.createUrlValue}/${this._editingId}` : this.createUrlValue
    try {
      const resp = await fetch(url, {
        method: this._editingId ? "PATCH" : "POST",
        headers: this._headers(),
        body: JSON.stringify(body)
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not save the expense")
      this._upsertRow(data)
      this._showToast(this._editingId ? "Expense updated" : "Expense added")
      this.closeModal()
    } catch (err) {
      this._showToast(err.message, "alert")
    } finally {
      btn.disabled = false
      btn.textContent = "Save expense"
    }
  }

  _dateValue() {
    const input = this.invoiceDateTarget
    if (input._flatpickr && input._flatpickr.selectedDates[0]) {
      const d = input._flatpickr.selectedDates[0]
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
    }
    return input.value
  }

  // ── Verify toggle (office) ──────────────────────────────────────────
  verify(e) { this._postStatus(e.currentTarget.dataset.id, "verify") }
  unverify(e) { this._postStatus(e.currentTarget.dataset.id, "unverify") }

  async _postStatus(id, action) {
    try {
      const resp = await fetch(`${this.createUrlValue}/${id}/${action}`, { method: "POST", headers: this._headers() })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not update the entry")
      this._upsertRow(data)
    } catch (err) {
      this._showToast(err.message, "alert")
    }
  }

  // ── Delete ──────────────────────────────────────────────────────────
  confirmDelete(e) {
    const row = e.currentTarget.closest("tr")
    const data = JSON.parse(row.dataset.expense || "{}")
    this._deleteId = data.id
    this.deleteNoteTarget.textContent = `"${data.vendor || "This entry"}" and its receipt files will be removed.`
    this.deleteModalTarget.classList.remove("hidden")
  }

  closeDelete() { this.deleteModalTarget.classList.add("hidden") }

  async doDelete() {
    try {
      const resp = await fetch(`${this.createUrlValue}/${this._deleteId}`, { method: "DELETE", headers: this._headers() })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not delete the entry")
      const row = this.rowTargets.find((tr) => tr.dataset.id === String(this._deleteId))
      if (row) row.remove()
      this.totalTarget.textContent = data.total
      this.countNoteTarget.textContent = data.count
      this._renumber()
      this._toggleEmpty()
      this._showToast("Expense deleted")
    } catch (err) {
      this._showToast(err.message, "alert")
    } finally {
      this.closeDelete()
    }
  }

  // ── Move to Sub Payments (Larry 2026-08-26) ─────────────────────────
  // The entry becomes an As-Needed sub payment for its vendor — receipts
  // ride along as the invoice's files, the row leaves this list.
  // ── Selection mode (Phelipe 2026-08-31): OFF by default — the Select
  // toggle reveals the checkboxes, then ticked rows move or delete. ─────
  toggleSelectMode() {
    this._selectMode = !this._selectMode
    if (!this._selectMode) {
      this.pickTargets.forEach((cb) => { cb.checked = false })
      if (this.hasPickAllTarget) this.pickAllTarget.checked = false
    }
    this._syncSelectMode()
    this._syncBulkBar()
  }

  _syncSelectMode() {
    const on = !!this._selectMode
    this.pickTargets.forEach((cb) => cb.classList.toggle("hidden", !on))
    if (this.hasPickAllTarget) this.pickAllTarget.classList.toggle("hidden", !on)
    // The Select pill swaps for the segmented bar, like Drawings & Specs.
    if (this.hasSelectToggleTarget) this.selectToggleTarget.classList.toggle("hidden", on)
    if (this.hasBulkBarTarget) {
      this.bulkBarTarget.classList.toggle("hidden", !on)
      this.bulkBarTarget.classList.toggle("inline-flex", on)
    }
  }

  pickChanged() { this._syncBulkBar() }

  // Select all = the rows you can see. The Find box hides rows with the
  // `hidden` class, which the old display check missed, so a filtered list
  // quietly picked the hidden rows too (Larry item 13, 2026-09-01).
  togglePickAll(e) {
    const on = e.currentTarget.checked
    this.pickTargets.forEach((cb) => { if (this._rowVisible(cb)) cb.checked = on })
    this._syncBulkBar()
  }

  _rowVisible(cb) {
    const tr = cb.closest("tr")
    return !!tr && !tr.classList.contains("hidden") && tr.style.display !== "none"
  }

  _picked() { return this.pickTargets.filter((cb) => cb.checked && this._rowVisible(cb)) }

  _syncBulkBar() {
    const n = this._picked().length
    if (this.hasBulkCountTarget) this.bulkCountTarget.textContent = `${n} selected`
    const arm = (btn, hoverClass) => {
      btn.classList.toggle("opacity-40", n === 0)
      btn.classList.toggle("cursor-not-allowed", n === 0)
      btn.classList.toggle("cursor-pointer", n > 0)
      btn.classList.toggle(hoverClass, n > 0)
    }
    if (this.hasBulkMoveBtnTarget) arm(this.bulkMoveBtnTarget, "hover:bg-blue-50")
    if (this.hasBulkDeleteBtnTarget) arm(this.bulkDeleteBtnTarget, "hover:bg-red-50")
  }

  confirmBulkDelete() {
    const rows = this._picked().map((cb) => JSON.parse(cb.closest("tr").dataset.expense || "{}"))
    if (!rows.length) return this._showToast("Tick at least one entry first.", "alert")
    const total = rows.reduce((sum, d) => sum + (Number(d.amount) || 0), 0)
    this.bulkDeleteNoteTarget.textContent =
      `${rows.length} ${rows.length === 1 ? "entry" : "entries"} ($${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}) leave the list along with their receipts. This can't be undone.`
    this.bulkDeleteModalTarget.classList.remove("hidden")
  }

  closeBulkDelete() { this.bulkDeleteModalTarget.classList.add("hidden") }

  async doBulkDelete() {
    const ids = this._picked().map((cb) => cb.closest("tr").dataset.id)
    if (!ids.length) return
    this.bulkDeleteGoTarget.disabled = true
    try {
      const resp = await fetch(`${this.createUrlValue}/delete_bulk`,
        { method: "POST", headers: this._headers(), body: JSON.stringify({ ids: ids }) })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not delete the entries")
      this._picked().forEach((cb) => { const tr = cb.closest("tr"); if (tr) tr.remove() })
      this.totalTarget.textContent = data.total
      this.countNoteTarget.textContent = data.count
      if (this.hasPickAllTarget) this.pickAllTarget.checked = false
      this._syncBulkBar()
      this._renumber()
      this._toggleEmpty()
      let msg = `Deleted ${data.deleted} ${data.deleted === 1 ? "entry" : "entries"}`
      if (data.skipped?.length) msg += ` — skipped: ${data.skipped.join(", ")}`
      this._showToast(msg, data.skipped?.length ? "alert" : "notice")
    } catch (err) {
      this._showToast(err.message, "alert")
    } finally {
      this.bulkDeleteGoTarget.disabled = false
      this.closeBulkDelete()
    }
  }

  confirmBulkMove() {
    const esc = (s) => (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    const rows = this._picked().map((cb) => JSON.parse(cb.closest("tr").dataset.expense || "{}"))
    if (!rows.length) return this._showToast("Tick at least one entry first.", "alert")
    // Order prefills by invoice date within each vendor (Phelipe
    // 2026-08-31) — oldest = 1, blanks last — and every box is editable.
    rows.sort((a, b) => (a.invoice_date || "9999").localeCompare(b.invoice_date || "9999") || (a.id - b.id))
    const counters = {}
    this.bulkMoveListTarget.innerHTML = rows.map((d) => {
      const key = (d.vendor || "").trim().toLowerCase()
      counters[key] = (counters[key] || 0) + 1
      const amt = d.amount ? `$${Number(d.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "—"
      const date = d.invoice_date || "—"
      return `<li class="flex items-center gap-2 px-3 py-1.5 text-[13px]">
        <input type="number" min="1" step="1" value="${counters[key]}" data-bulk-seq data-id="${d.id}"
               title="Order" class="w-14 border border-gray-300 rounded-lg px-1.5 py-1 text-[13px] text-center shrink-0">
        <span class="truncate flex-1 min-w-0">${esc(d.vendor || "(no vendor)")}${d.invoice_number ? ` <span class="text-gray-400">#${esc(d.invoice_number)}</span>` : ""}</span>
        <span class="w-24 text-right shrink-0 text-gray-500 tabular-nums whitespace-nowrap">${esc(date)}</span>
        <span class="w-28 text-right shrink-0 tabular-nums whitespace-nowrap">${amt}</span>
      </li>`
    }).join("")
    if (this.hasBulkMovePaidTarget) this.bulkMovePaidTarget.checked = true
    this.bulkMoveModalTarget.classList.remove("hidden")
  }

  closeBulkMove() { this.bulkMoveModalTarget.classList.add("hidden") }

  async doBulkMove() {
    const ids = this._picked().map((cb) => cb.closest("tr").dataset.id)
    if (!ids.length) return
    this.bulkMoveGoTarget.disabled = true
    try {
      const status = this.hasBulkMovePaidTarget && this.bulkMovePaidTarget.checked ? "paid" : "received"
      const sequences = {}
      this.bulkMoveListTarget.querySelectorAll("[data-bulk-seq]").forEach((el) => {
        if (el.value.trim() !== "") sequences[el.dataset.id] = el.value.trim()
      })
      const resp = await fetch(`${this.createUrlValue}/move_bulk`,
        { method: "POST", headers: this._headers(), body: JSON.stringify({ ids: ids, invoice_status: status, sequences: sequences }) })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not move the entries")
      this._picked().forEach((cb) => { const tr = cb.closest("tr"); if (tr) tr.remove() })
      this.totalTarget.textContent = data.total
      this.countNoteTarget.textContent = data.count
      if (this.hasPickAllTarget) this.pickAllTarget.checked = false
      this._syncBulkBar()
      this._renumber()
      this._toggleEmpty()
      let msg = `Moved ${data.moved} ${data.moved === 1 ? "entry" : "entries"} to Sub Payments`
      if (data.skipped?.length) msg += ` — skipped: ${data.skipped.join(", ")}`
      this._showToast(msg, data.skipped?.length ? "alert" : "notice")
    } catch (err) {
      this._showToast(err.message, "alert")
    } finally {
      this.bulkMoveGoTarget.disabled = false
      this.closeBulkMove()
    }
  }

  // Confirm-and-correct popup (Phelipe 2026-08-31): the expense's details
  // prefill and stay editable, the missing invoice file can be dropped in,
  // and "Already paid" is the default — an internal expense is money spent.
  confirmMove(e) {
    const row = e.currentTarget.closest("tr")
    const data = JSON.parse(row.dataset.expense || "{}")
    this._moveId = data.id
    this._moveFiles = []
    if (this.hasMoveChipsTarget) this.moveChipsTarget.innerHTML = ""
    if (this.hasMoveVendorTarget) this.moveVendorTarget.value = data.vendor || ""
    this._lookupMoveMatch()
    if (this.hasMoveAmountTarget) this.moveAmountTarget.value = data.amount ? Number(data.amount).toFixed(2) : ""
    if (this.hasMoveNumberTarget) this.moveNumberTarget.value = data.invoice_number || ""
    if (this.hasMoveDateTarget) {
      this.moveDateTarget.value = data.invoice_date || ""
      this.moveDateTarget._flatpickr?.setDate(data.invoice_date || "", false)
    }
    if (this.hasMoveDescTarget) this.moveDescTarget.value = data.description || ""
    if (this.hasMovePaidTarget) this.movePaidTarget.checked = true
    if (this.hasMoveSeqTarget) this.moveSeqTarget.value = ""
    const receipts = Array.isArray(data.receipts) ? data.receipts.length : 0
    this.moveNoteTarget.textContent = receipts
      ? `Becomes an As-Needed sub payment — its ${receipts === 1 ? "file rides" : `${receipts} files ride`} along, and the expense leaves this list. Double-check the details:`
      : "Becomes an As-Needed sub payment and leaves this list. No file is on this entry — attach the invoice below if you have it:"
    this.moveModalTarget.classList.remove("hidden")
  }

  closeMove() { this.moveModalTarget.classList.add("hidden") }

  moveBrowse() { this.moveInputTarget.click() }
  moveChosen(e) { Array.from(e.target.files || []).forEach((f) => this._moveUpload(f)); e.target.value = "" }
  moveDragOver(e) { e.preventDefault(); this.moveZoneTarget.classList.add("border-brand", "bg-brand/5") }
  moveDragLeave() { this.moveZoneTarget.classList.remove("border-brand", "bg-brand/5") }
  moveDrop(e) {
    e.preventDefault()
    this.moveDragLeave()
    Array.from(e.dataTransfer?.files || []).forEach((f) => this._moveUpload(f))
  }

  async _moveUpload(file) {
    const esc = (s) => (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    const chip = document.createElement("span")
    chip.className = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-200 text-[13px] text-blue-700 max-w-full"
    chip.innerHTML = `<svg class="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg><span class="truncate max-w-[200px]">${esc(file.name)}</span>`
    this.moveChipsTarget.appendChild(chip)
    try {
      const presignResp = await fetch(this.presignUrlValue, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
      })
      const presign = await presignResp.json()
      if (!presignResp.ok || !presign.uploads?.[0]) throw new Error(presign.error || "Could not start the upload")
      const put = await fetch(presign.uploads[0].upload_url, { method: "PUT", body: file })
      if (!put.ok) throw new Error("Upload failed — try again")
      this._moveFiles.push({ cache_name: presign.uploads[0].cache_name, filename: file.name })
      chip.className = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 border border-gray-200 text-[13px] text-gray-700 max-w-full"
      chip.innerHTML = `<span class="truncate max-w-[200px]">${esc(file.name)}</span>`
    } catch (err) {
      chip.remove()
      this._showToast(err.message, "alert")
    }
  }

  // Smart match (Larry 2026-09-01): as the vendor name settles, ask which
  // existing sub it files under and say so — the GC sees "Strata → STRATA,
  // Inc." before clicking Move, with a tick-box for a real new company.
  moveVendorChanged() {
    clearTimeout(this._matchTimer)
    this._matchTimer = setTimeout(() => this._lookupMoveMatch(), 350)
  }

  async _lookupMoveMatch() {
    if (!this.hasMoveMatchTarget || !this.matchUrlValue) return
    const name = this.hasMoveVendorTarget ? this.moveVendorTarget.value.trim() : ""
    const show = (text, matched) => {
      this.moveMatchTarget.textContent = text
      this.moveMatchTarget.classList.toggle("hidden", !text)
      this.moveMatchTarget.classList.toggle("text-green-700", matched)
      this.moveMatchTarget.classList.toggle("text-gray-500", !matched)
      if (this.hasMoveForceNewRowTarget) this.moveForceNewRowTarget.classList.toggle("hidden", !matched)
      if (!matched && this.hasMoveForceNewTarget) this.moveForceNewTarget.checked = false
    }
    if (!name) return show("", false)
    show("Checking for an existing sub…", false)
    try {
      const resp = await fetch(`${this.matchUrlValue}?name=${encodeURIComponent(name)}`, { headers: { "Accept": "application/json" } })
      const data = await resp.json()
      if (data.contract) show(`Files under the existing sub "${data.contract.name}"`, true)
      else show("New sub — nothing on this project matches this name yet", false)
    } catch { show("", false) }
  }

  async doMove() {
    if (this.hasMoveBtnTarget) this.moveBtnTarget.disabled = true
    try {
      const body = {
        vendor: this.hasMoveVendorTarget ? this.moveVendorTarget.value.trim() : undefined,
        number: this.hasMoveNumberTarget ? this.moveNumberTarget.value.trim() : undefined,
        invoice_date: this.hasMoveDateTarget ? this.moveDateTarget.value.trim() : undefined,
        amount: this.hasMoveAmountTarget ? this.moveAmountTarget.value.trim() : undefined,
        description: this.hasMoveDescTarget ? this.moveDescTarget.value.trim() : undefined,
        invoice_status: this.hasMoveReviewTarget && this.moveReviewTarget.checked ? "received" : "paid",
        sequence: this.hasMoveSeqTarget && this.moveSeqTarget.value.trim() !== "" ? this.moveSeqTarget.value.trim() : undefined,
        extra_files: this._moveFiles || [],
        force_new: this.hasMoveForceNewTarget && this.moveForceNewTarget.checked ? "1" : ""
      }
      const resp = await fetch(`${this.createUrlValue}/${this._moveId}/move_to_sub_payments`,
        { method: "POST", headers: this._headers(), body: JSON.stringify(body) })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not move the entry")
      const row = this.rowTargets.find((tr) => tr.dataset.id === String(this._moveId))
      if (row) row.remove()
      this.totalTarget.textContent = data.total
      this.countNoteTarget.textContent = data.count
      this._renumber()
      this._toggleEmpty()
      this._showToast(`Moved to Sub Payments — filed under ${data.contract_name}`)
    } catch (err) {
      this._showToast(err.message, "alert")
    } finally {
      if (this.hasMoveBtnTarget) this.moveBtnTarget.disabled = false
      this.closeMove()
    }
  }

  // ── Receipt uploads: presign → PUT → remember for save ──────────────
  pickFiles() { this.fileInputTarget.click() }
  dragOver(e) { e.preventDefault(); this.dropzoneTarget.classList.add("border-blue-400", "bg-blue-50") }
  dragLeave() { this.dropzoneTarget.classList.remove("border-blue-400", "bg-blue-50") }
  drop(e) {
    e.preventDefault()
    this.dragLeave()
    this._uploadAll(Array.from(e.dataTransfer.files || []))
  }
  filesChosen(e) {
    this._uploadAll(Array.from(e.target.files || []))
    e.target.value = ""
  }

  async _uploadAll(files) {
    for (const file of files) await this._uploadOne(file)
  }

  async _uploadOne(file) {
    const chip = this._makeChip(file.name, true)
    this.chipsTarget.appendChild(chip)
    try {
      const presign = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type, byte_size: file.size }] })
      })
      const pd = await presign.json()
      if (!presign.ok) throw new Error(pd.error || "Upload refused")
      const up = pd.uploads[0]
      const put = await fetch(up.upload_url, { method: "PUT", body: file })
      if (!put.ok) throw new Error(`Upload failed for ${file.name}`)
      this._files.push({ cache_name: up.cache_name, filename: up.filename })
      this._finishChip(chip, () => {
        this._files = this._files.filter((f) => f.cache_name !== up.cache_name)
        chip.remove()
      })
    } catch (err) {
      chip.remove()
      this._showToast(err.message, "alert")
    }
  }

  _makeChip(name, uploading) {
    const chip = document.createElement("span")
    chip.className = uploading
      ? "inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-blue-50 text-blue-700 text-[13px]"
      : "inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-gray-100 text-gray-700 text-[13px]"
    chip.innerHTML = uploading
      ? `<span class="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></span><span class="truncate max-w-[180px]">${name}</span>`
      : `<span class="truncate max-w-[180px]">${name}</span>`
    return chip
  }

  _finishChip(chip, onRemove) {
    const name = chip.querySelector("span.truncate").textContent
    chip.className = "inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-gray-100 text-gray-700 text-[13px]"
    chip.innerHTML = `<span class="truncate max-w-[180px]">${name}</span>`
    const x = document.createElement("button")
    x.textContent = "×"
    x.className = "text-gray-400 hover:text-red-600 cursor-pointer font-medium"
    x.addEventListener("click", onRemove)
    chip.appendChild(x)
  }

  _addExistingChip(receipt) {
    const chip = this._makeChip(receipt.filename, false)
    const x = document.createElement("button")
    x.textContent = "×"
    x.className = "text-gray-400 hover:text-red-600 cursor-pointer font-medium"
    x.addEventListener("click", async () => {
      await fetch(`${this.createUrlValue}/${this._editingId}/attachments/${receipt.id}`, { method: "DELETE", headers: this._headers() })
      chip.remove()
    })
    chip.appendChild(x)
    this.chipsTarget.appendChild(chip)
  }

  // ── Row plumbing ────────────────────────────────────────────────────
  _upsertRow(data) {
    const existing = this.rowTargets.find((tr) => tr.dataset.id === String(data.id))
    if (existing) {
      existing.outerHTML = data.row_html
    } else {
      const tmp = document.createElement("tbody")
      tmp.innerHTML = data.row_html
      const newRow = tmp.firstElementChild
      const after = this.rowTargets.find((tr) => tr.dataset.sort > newRow.dataset.sort)
      if (after) this.tbodyTarget.insertBefore(newRow, after)
      else this.tbodyTarget.appendChild(newRow)
    }
    this.totalTarget.textContent = data.total
    this.countNoteTarget.textContent = data.count
    this._renumber()
    this._toggleEmpty()
  }

  _renumber() {
    let n = 0
    this.rowTargets.forEach((tr) => {
      if (tr.classList.contains("hidden")) return
      n += 1
      const cell = tr.querySelector("[data-num]")
      if (cell) cell.textContent = n
    })
    // Rows inserted by AJAX arrive with the checkbox hidden — keep them in
    // step with the current selection mode.
    if (this._selectMode) this._syncSelectMode()
    this._syncFilterBox()
  }

  _toggleEmpty() {
    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("hidden", this.rowTargets.length > 0)
  }

  _headers() {
    return {
      "Content-Type": "application/json", "Accept": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
    }
  }

  // ── Bulk AI import (Phelipe 2026-07-28): drop one file — spreadsheet,
  // PDF, or receipt photos — AI fills the whole table. "Don't add
  // duplicates" skips rows matching an existing entry. ──
  openAiImport() {
    this._aiImportFile = null
    if (this.hasAiImportStatusTarget) { this.aiImportStatusTarget.textContent = ""; this.aiImportStatusTarget.classList.add("hidden") }
    if (this.hasAiImportErrorTarget) this.aiImportErrorTarget.classList.add("hidden")
    if (this.hasAiImportBtnTarget) { this.aiImportBtnTarget.disabled = true; this.aiImportBtnTarget.textContent = "Import" }
    if (this.hasAiImportFileInputTarget) this.aiImportFileInputTarget.value = ""
    this.aiImportModalTarget.classList.remove("hidden")
  }

  closeAiImport() { if (this.hasAiImportModalTarget) this.aiImportModalTarget.classList.add("hidden") }

  // "Enter manually instead" — the merged Add Expenses modal's escape hatch
  // to the classic one-row form (Larry 2026-08-06).
  manualFromImport() {
    this.closeAiImport()
    this.openNew()
  }

  aiImportZoneClick() { this.aiImportFileInputTarget.click() }

  aiImportDragOver(e) {
    e.preventDefault()
    this.aiImportZoneTarget.classList.add("border-purple-500", "bg-purple-100")
  }

  aiImportDragLeave() {
    this.aiImportZoneTarget.classList.remove("border-purple-500", "bg-purple-100")
  }

  aiImportDrop(e) {
    e.preventDefault()
    this.aiImportDragLeave()
    const file = e.dataTransfer?.files?.[0]
    if (file) this._aiImportUpload(file)
  }

  aiImportFileChosen(e) {
    const file = e.target.files?.[0]
    if (file) this._aiImportUpload(file)
  }

  async _aiImportUpload(file) {
    const status = this.hasAiImportStatusTarget ? this.aiImportStatusTarget : null
    if (status) { status.textContent = `Uploading ${file.name}…`; status.classList.remove("hidden") }
    if (this.hasAiImportErrorTarget) this.aiImportErrorTarget.classList.add("hidden")
    try {
      const presign = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type, byte_size: file.size }] })
      })
      const pd = await presign.json()
      if (!presign.ok) throw new Error(pd.error || "Upload refused")
      const up = pd.uploads[0]
      const put = await fetch(up.upload_url, { method: "PUT", body: file })
      if (!put.ok) throw new Error(`Upload failed for ${file.name}`)
      this._aiImportFile = { cache_name: up.cache_name, filename: up.filename }
      if (status) status.textContent = `${file.name} — ready to import.`
      if (this.hasAiImportBtnTarget) this.aiImportBtnTarget.disabled = false
    } catch (err) {
      if (status) status.classList.add("hidden")
      this._showToast(err.message, "alert")
    }
  }

  async submitAiImport() {
    if (!this._aiImportFile) return
    const btn = this.hasAiImportBtnTarget ? this.aiImportBtnTarget : null
    if (btn) { btn.disabled = true; btn.textContent = "Importing…" }
    try {
      const resp = await fetch(this.aiImportUrlValue, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify({
          cache_name: this._aiImportFile.cache_name,
          filename: this._aiImportFile.filename,
          skip_duplicates: (this.hasAiImportSkipTarget && this.aiImportSkipTarget.checked) ? "1" : "0"
        })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not import the file.")
      ;(data.rows_html || []).forEach((html) => this.tbodyTarget.insertAdjacentHTML("afterbegin", html))
      if (data.rows_html?.length && this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")
      if (this.hasTotalTarget && data.total) this.totalTarget.textContent = data.total
      if (this.hasCountNoteTarget && data.count != null) this.countNoteTarget.textContent = data.count
      this._renumber()
      this.closeAiImport()
      const skipped = data.skipped ? ` · ${data.skipped} duplicate${data.skipped === 1 ? "" : "s"} skipped` : ""
      const missing = data.missing_amount
        ? ` · ${data.missing_amount} highlighted in yellow — enter the missing amount${data.missing_amount === 1 ? "" : "s"}`
        : ""
      this._showToast(`Imported ${data.added} expense${data.added === 1 ? "" : "s"}${skipped}${missing}`)
    } catch (err) {
      if (this.hasAiImportErrorTarget) {
        this.aiImportErrorTarget.textContent = err.message
        this.aiImportErrorTarget.classList.remove("hidden")
      }
    } finally {
      if (btn) { btn.disabled = !this._aiImportFile; btn.textContent = "Import" }
    }
  }

  _showToast(message, type = "notice") {
    const div = document.createElement("div")
    div.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    div.setAttribute("data-controller", "flash")
    div.setAttribute("data-flash-dismiss-after-value", "5000")
    div.textContent = message
    document.body.prepend(div)
  }
}
